import React, { useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

interface ChartComponentProps {
  type: 'line' | 'bar' | 'pie';
  data: ChartData<any>;
  options?: ChartOptions<any>;
  id: string; // Unique ID for each chart instance
}

const ChartComponent: React.FC<ChartComponentProps> = ({ type, data, options, id }) => {
  const chartRef = useRef<ChartJS | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    // Cleanup function to destroy the chart instance
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [id]); // Re-run cleanup when chart ID changes

  const renderChart = () => {
    // Ensure previous instance is destroyed
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    // Create new chart instance
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      chartRef.current = new ChartJS(ctx, {
        type,
        data,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          ...options,
        },
      });
    }
  };

  useEffect(() => {
    renderChart();
    // Cleanup on unmount or data/options change
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [data, options, type]);

  return (
    <div style={{ width: '100%', height: '300px', position: 'relative' }}>
      <canvas 
        ref={canvasRef}
        id={id} // Use unique ID for each chart
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
};

export default ChartComponent; 